import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { T } from "@/lib/i18n";

const SCRIPTS = [
  "https://code.jquery.com/jquery-3.7.1.min.js",
  "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js",
  "https://widget.mondialrelay.com/parcelshop-picker/jquery.plugin.mondialrelay.parcelshoppicker.min.js",
];
let loading = null;

function loadScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) return res();
    const s = document.createElement("script");
    s.src = src;
    s.onload = res;
    s.onerror = rej;
    document.body.appendChild(s);
  });
}

function loadWidget() {
  if (!loading) {
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(l);
    }
    loading = SCRIPTS.reduce((p, src) => p.then(() => loadScript(src)), Promise.resolve());
  }
  return loading;
}

function Widget({ kind, onSelect, countries }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadWidget()
      .then(() => {
        if (cancelled || !ref.current || !window.jQuery?.fn?.MR_ParcelShopPicker) return;
        ref.current.innerHTML = "";
        window.jQuery(ref.current).MR_ParcelShopPicker({
          Target: "#mr-target",
          Brand: process.env.REACT_APP_MR_BRAND,
          Country: countries?.[0] || "FR",
          ...(countries?.length ? { AllowedCountries: countries.join(",") } : {}),
          ColLivMod: kind === "locker" ? "APM" : "24R",
          NbResults: 7,
          Responsive: true,
          ShowResultsOnMap: true,
          OnParcelShopSelected: (d) =>
            onSelect({
              id: `${d?.Pays || "FR"}-${d?.ID || ""}`,
              name: d?.Nom || "",
              address: [d?.Adresse1, d?.Adresse2].filter(Boolean).join(" "),
              zip: d?.CP || "",
              city: d?.Ville || "",
              country: d?.Pays || "FR",
              kind,
            }),
        });
        setReady(true);
      })
      .catch(() => setErr(true));
    return () => {
      cancelled = true;
    };
  }, [kind, onSelect, countries]);

  return (
    <div className="relative min-h-[420px] rounded-sm bg-white text-black">
      {!ready && !err && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[var(--plum)]" /></div>}
      {err && <p className="p-6 text-sm"><T>Le service Mondial Relay est momentanément indisponible.</T></p>}
      <div ref={ref} data-testid="mr-widget" />
      <input type="hidden" id="mr-target" />
    </div>
  );
}

export function MondialRelayPicker({ open, onOpenChange, onChoose, countries = null }) {
  const [kind, setKind] = useState("point");
  const [picked, setPicked] = useState(null);

  const tab = (k, label, Icon) => (
    <button data-testid={`mr-kind-${k}`} onClick={() => { setKind(k); setPicked(null); }} className={`flex flex-1 items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-xs uppercase tracking-[0.18em] transition-colors ${kind === k ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--ink)]" : "border-[var(--line)] text-[var(--parchment)]"}`}>
      <Icon className="h-4 w-4" /> <T>{label}</T>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mr-dialog" data-lenis-prevent className="max-h-[92vh] max-w-4xl overflow-y-auto border-[var(--line)] bg-[var(--ink)] text-[var(--parchment)]">
        <DialogHeader><DialogTitle className="font-display text-3xl italic"><T>Choisir mon retrait Mondial Relay</T></DialogTitle></DialogHeader>
        <div className="flex gap-3">
          {tab("point", "Point Relais", MapPin)}
          {tab("locker", "Locker", Package)}
        </div>
        {countries ? <p data-testid="mr-country-hint" className="text-xs text-[var(--parchment)]/80"><T>Choisissez d'abord votre pays dans la liste du module, puis votre code postal.</T></p> : null}
        <Widget key={`${kind}-${countries ? countries.join("") : "FR"}`} kind={kind} onSelect={setPicked} countries={countries} />
        <div className="flex flex-col items-start justify-between gap-4 border-t border-[var(--line)] pt-4 sm:flex-row sm:items-center">
          <p data-testid="mr-picked" className="text-sm text-[var(--muted)]">
            {picked ? <><span className="text-[var(--gold-soft)]">{picked.name}</span> — {picked.address}, {picked.zip} {picked.city}</> : <T>Sélectionnez un point sur la carte ou dans la liste.</T>}
          </p>
          <button data-testid="mr-confirm" disabled={!picked} onClick={() => { onChoose(picked); onOpenChange(false); }} className="h-12 shrink-0 rounded-full bg-[var(--gold)] px-8 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] disabled:opacity-40">
            <T>Valider ce point</T>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
